import { endOfMonth, parse } from "date-fns";
import { DATEFORMAT } from "src/js/util/dates";
import { ftsqToMsq } from "src/js/util/units_and_constants";
import { disabledEPCFields, disabledZooplaFields } from "./DisabledFilters";
import { hiddenFieldsByFeature } from "./hiddenFields";
import { path, prop } from "ramda";
import { ComparableTile, ZooplaComparableTileProperties } from "react-migration/lib/typings/ComparablesTile";
import { IComparablesTransactionDTO } from "react-migration/lib/typings/comparables/Comparables";
import { ZooplaComparableDTO, ZooplaPropertySubTypeGQL } from "react-migration/lib/typings/Zoopla";
import {
  ComparablesFilterOptions,
  HMLRComparablesFilterOptions,
  HomeTrackComparablesFilterOptions,
} from "../typings/Comparables";
import { ZooplaPriceModes, PriceModes } from "react-migration/domains/comparables/typings/PriceModes";
import { ComparablesFilterFields } from "../components/Filters/FiltersWithClear";
import { HMLRTenureShorthand } from "react-migration/lib/typings/comparables/HMLR";
import { HometrackTenureShorthandGQL } from "react-migration/lib/typings/comparables/Hometrack";
import { get } from "lodash";
import { EnrichedComparableDTO } from "react-migration/lib/typings/EnrichedComparables";
import hasBetaFeature from "src/js/stores/user/actions/hasBetaFeature";
import Feature from "src/js/stores/user/Feature";
import hasFeature from "src/js/stores/user/actions/hasFeature";
import { exactMatch, gatherConditionsAndZooplaPropertySubTypes, possibleUKCombinations } from "./PropertyTypes";

import { isZoopla } from "./isZoopla";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const emptyArray: any[] = [];

function createFilterObject<T, X>(o: {
  [P in keyof T]: null | ((value: T[P]) => ((item: X) => boolean)[]);
}) {
  return o;
}

type NullishString = string | undefined | null;
const getZooplaDTORentalLastValueDate = path<NullishString>(["valuation", "rent", "lastValueDate"]);
const getZooplaDTOSaleLastValueDate = path<NullishString>(["valuation", "sale", "lastValueDate"]);
const getZooplaTileRentalLastValueDate = prop("valuation.rent.lastValueDate");
const getZooplaTileSaleLastValueDate = prop("valuation.sale.lastValueDate");

function createZooplaFilterObject<T, X>(o: {
  [P in keyof T]: null | ((value: T[P], priceMode?: ZooplaPriceModes) => ((item: X) => boolean)[]);
}) {
  return o;
}
export const filterTypes = createFilterObject<
  HMLRComparablesFilterOptions,
  ComparableTile["properties"] | IComparablesTransactionDTO
>({
  dateFrom: (value) =>
    value
      ? [
          (item) => {
            const minDate = parse(`01/${value}`, DATEFORMAT, new Date());
            const transferDate = new Date(`${item.date_of_transfer.replace(/T\d\d:\d\d:\d\d\..*$/, "")}T23:59:59.999Z`);
            return transferDate.valueOf() > minDate.valueOf();
          },
        ]
      : [],
  dateTo: (value) =>
    value
      ? [
          (item) => {
            const maxDate = endOfMonth(parse(`01/${value}`, DATEFORMAT, new Date()));
            const transferDate = new Date(`${item.date_of_transfer.replace(/T\d\d:\d\d:\d\d\..*$/, "")}T00:00:00.000Z`);
            return transferDate.valueOf() < maxDate.valueOf();
          },
        ]
      : [],
  dcsConditions: (value) =>
    value
      ? [
          (item) => {
            const result = value.some(({ designation, category, sub_category }) => {
              return (
                item.designation === (designation ?? item.designation) &&
                item.category === (category ?? item.category) &&
                item.sub_category === (sub_category ?? item.sub_category)
              );
            });

            return result;
          },
        ]
      : [],
  floorArea: (value) => {
    const { min, max } = (value as { min: number; max: number }) || {};
    const filters: Array<(item: ComparableTile["properties"] | IComparablesTransactionDTO) => boolean> = [];

    if (typeof min === "number") {
      filters.push((properties) => (properties?.total_floor_area ?? -Infinity) >= min);
    }

    if (typeof max === "number") {
      filters.push((properties) => (properties?.total_floor_area ?? Infinity) <= max);
    }

    return filters;
  },
  newBuildsOnly: (value) => (value ? [(item) => item.is_new_property === value] : []),
  tenure: (value) => [
    (item) => {
      // TODO: Remove once hometrackv2 beta is done. This code sucks
      // but its a hack hack hack for a short term
      const tenure = Array.isArray(value)
        ? value
        : // Sorry
          [...((value === "Any" ? ["F", "L"] : [value]) as HMLRTenureShorthand[])];

      return !!item.tenure && tenure.includes(item.tenure as HMLRTenureShorthand);
    },
  ],
});

export const zooplaFilterTypes = createZooplaFilterObject<HomeTrackComparablesFilterOptions, ZooplaComparableDTO>({
  bedrooms: (value) => {
    const { min, max } = (value as { min: number; max: number }) || {};
    const filters: Array<(item: ZooplaComparableDTO) => boolean> = [];

    if (typeof min === "number") {
      filters.push((properties) => (properties?.attributes?.bedrooms ?? -Infinity) >= min);
    }

    if (typeof max === "number") {
      filters.push((properties) => (properties?.attributes?.bedrooms ?? Infinity) <= max);
    }

    return filters;
  },
  dateFrom: (value, priceMode) => {
    const getter = /rental/i.test(priceMode ?? "") ? getZooplaDTORentalLastValueDate : getZooplaDTOSaleLastValueDate;
    return value
      ? [
          (item) => {
            const minDate = parse(`01/${value}`, DATEFORMAT, new Date());
            const dateString = getter(item);
            const transferDate = new Date(`${dateString?.replace?.(/T\d\d:\d\d:\d\d\..*$/, "")}T23:59:59.999Z`);
            return !!dateString && transferDate.valueOf() > minDate.valueOf();
          },
        ]
      : [];
  },
  dateTo: (value, priceMode) => {
    const getter = /rental/i.test(priceMode ?? "") ? getZooplaDTORentalLastValueDate : getZooplaDTOSaleLastValueDate;
    return value
      ? [
          (item) => {
            const maxDate = endOfMonth(parse(`01/${value}`, DATEFORMAT, new Date()));
            const dateString = getter(item);
            const transferDate = new Date(`${dateString?.replace?.(/T\d\d:\d\d:\d\d\..*$/, "")}T00:00:00.000Z`);
            return !!dateString && transferDate.valueOf() < maxDate.valueOf();
          },
        ]
      : [];
  },
  dcsConditions: (value) => {
    if (!value) {
      return [];
    }

    const propertySubTypes = new Set(value.map((v) => v.sub_category as ZooplaPropertySubTypeGQL));
    return [
      (properties) => {
        return propertySubTypes.has(get(properties, "attributes.propertySubType") as ZooplaPropertySubTypeGQL);
      },
    ];
  },
  floorArea: (value) => {
    const { min, max } = (value as { min: number; max: number }) || {};
    const filters: Array<(item: ZooplaComparableDTO) => boolean> = [];

    if (typeof min === "number") {
      filters.push((properties) => ftsqToMsq(properties?.attributes.floorArea ?? -Infinity) >= min);
    }

    if (typeof max === "number") {
      filters.push((properties) => ftsqToMsq(properties?.attributes?.floorArea ?? Infinity) <= max);
    }

    return filters;
  },
  tenure: (value) => [
    (item) => {
      // TODO: Remove once hometrackv2 beta is done. This code sucks
      // but its a hack hack hack for a short term
      const tenure = Array.isArray(value)
        ? value
        : // Sorry
          [...((value === "Any" ? ["F", "L"] : [value]) as HMLRTenureShorthand[])];

      return !!item.attributes.tenure && tenure.includes(item.attributes.tenure[0] as HMLRTenureShorthand);
    },
  ],
  yearBuiltFrom: (value) => {
    return value
      ? [
          (item) => {
            const year = item.attributes.yearBuilt;
            return year ? year >= value : false;
          },
        ]
      : [];
  },
  yearBuiltTo: (value) => {
    return value
      ? [
          (item) => {
            const year = item.attributes.yearBuilt;
            return year ? year <= value : false;
          },
        ]
      : [];
  },
});

export const zooplaTileFilterTypes = createZooplaFilterObject<
  HomeTrackComparablesFilterOptions & { newBuildsOnly: boolean },
  ZooplaComparableTileProperties
>({
  // TODO: Remove once hometrackv2 beta is done.
  newBuildsOnly: (value) => (value ? [(properties) => get(properties, "attributes.newBuild") === value] : []),
  bedrooms: (value) => {
    const { min, max } = (value as { min: number; max: number }) || {};
    const filters: Array<(item: ZooplaComparableTileProperties) => boolean> = [];

    if (typeof min === "number") {
      filters.push((properties) => (get(properties, "attributes.bedrooms") ?? -Infinity) >= min);
    }

    if (typeof max === "number") {
      filters.push((properties) => (get(properties, "attributes.bedrooms") ?? Infinity) <= max);
    }

    return filters;
  },
  dateFrom: (value, priceMode) => {
    const getter = /rental/i.test(priceMode ?? "") ? getZooplaTileRentalLastValueDate : getZooplaTileSaleLastValueDate;
    return value
      ? [
          (item) => {
            const minDate = parse(`01/${value}`, DATEFORMAT, new Date());
            const dateString = getter(item as Record<"valuation.rent.lastValueDate", string>);
            const transferDate = new Date(`${dateString?.replace?.(/T\d\d:\d\d:\d\d\..*$/, "")}T23:59:59.999Z`);
            return !!dateString && transferDate.valueOf() > minDate.valueOf();
          },
        ]
      : [];
  },
  dateTo: (value, priceMode) => {
    const getter = /rental/i.test(priceMode ?? "") ? getZooplaTileRentalLastValueDate : getZooplaTileSaleLastValueDate;
    return value
      ? [
          (item) => {
            const maxDate = endOfMonth(parse(`01/${value}`, DATEFORMAT, new Date()));
            const dateString = getter(item as Record<"valuation.rent.lastValueDate", string>);
            const transferDate = new Date(`${dateString?.replace?.(/T\d\d:\d\d:\d\d\..*$/, "")}T00:00:00.000Z`);
            return !!dateString && transferDate.valueOf() < maxDate.valueOf();
          },
        ]
      : [];
  },
  dcsConditions: (value) => {
    if (!value) {
      return [];
    }

    const hometrackv2 = hasBetaFeature(Feature.hometrackv2) || hasFeature(Feature.zooplaFullAccess);
    if (!hometrackv2) {
      const mappableTypes = gatherConditionsAndZooplaPropertySubTypes(possibleUKCombinations[1]);
      const t = mappableTypes.flatMap(({ dcsConditions, zooplaPropertySubTypes }) => {
        if (value.some((selected) => dcsConditions.some((test) => exactMatch(selected, test)))) {
          return zooplaPropertySubTypes.map(({ propertySubType }) => propertySubType);
        }
      });

      return [
        (properties) => {
          return t.some((val) => val === (get(properties, "attributes.propertySubType") as ZooplaPropertySubTypeGQL));
        },
      ];
    }

    const propertySubTypes = new Set(value.map((v) => v.sub_category as ZooplaPropertySubTypeGQL));
    return [
      (properties) => {
        return propertySubTypes.has(get(properties, "attributes.propertySubType") as ZooplaPropertySubTypeGQL);
      },
    ];
  },
  floorArea: (value) => {
    const { min, max } = (value as { min: number; max: number }) || {};
    const filters: Array<(item: ZooplaComparableTileProperties) => boolean> = [];

    if (typeof min === "number") {
      filters.push(
        (properties) => ftsqToMsq(parseFloat(get(properties, "attributes.floorArea") ?? "-Infinity")) >= min
      );
    }

    if (typeof max === "number") {
      filters.push((properties) => ftsqToMsq(parseFloat(get(properties, "attributes.floorArea") ?? "Infinity")) <= max);
    }

    return filters;
  },
  tenure: (value) => {
    return [
      (item) => {
        // TODO: Remove once hometrackv2 beta is done. This code sucks
        // but its a hack hack hack for a short term
        const tenure = Array.isArray(value)
          ? value
          : // Sorry
            [...((value === "Any" ? ["F", "L", "S"] : [value]) as HometrackTenureShorthandGQL[])];

        return (
          !!get(item, "attributes.tenure") &&
          tenure.includes(get(item, "attributes.tenure")![0] as HometrackTenureShorthandGQL)
        );
      },
    ];
  },
  yearBuiltFrom: (value) => {
    return value
      ? [
          (item) => {
            const year = get(item, "attributes.yearBuilt");
            return year ? year >= value : false;
          },
        ]
      : [];
  },
  yearBuiltTo: (value) => {
    return value
      ? [
          (item) => {
            const year = get(item, "attributes.yearBuilt");
            return year ? year <= value : false;
          },
        ]
      : [];
  },
});

export function createZooplaFilters(
  filters: ComparablesFilterOptions | undefined,
  exclusions: Set<ComparablesFilterFields>,
  priceMode?: ZooplaPriceModes
): ((item: ZooplaComparableDTO) => boolean)[] {
  if (!filters) {
    return emptyArray;
  }

  const include = (filter: ComparablesFilterFields): boolean => !exclusions?.has(filter);

  return Object.entries(filters).flatMap(
    ([key, value]) =>
      (include(key as ComparablesFilterFields) &&
        zooplaFilterTypes[key as keyof ComparablesFilterOptions]?.(value as never, priceMode)) ||
      []
  );
}

export function createZooplaTileFilters(
  filters: ComparablesFilterOptions | undefined,
  exclusions: Set<ComparablesFilterFields>,
  priceMode: ZooplaPriceModes
): ((item: ZooplaComparableTileProperties) => boolean)[] {
  if (!filters) {
    return emptyArray;
  }

  const include = (filter: ComparablesFilterFields): boolean => {
    return !exclusions?.has(filter);
  };

  return Object.entries(filters).flatMap(
    ([key, value]) =>
      (include(key as ComparablesFilterFields) &&
        zooplaTileFilterTypes[key as keyof ComparablesFilterOptions]?.(value as never, priceMode)) ||
      []
  );
}

export function createFilters(
  filters: ComparablesFilterOptions | undefined,
  exclusions: Set<ComparablesFilterFields>
): ((item: ComparableTile["properties"] | IComparablesTransactionDTO) => boolean)[] {
  if (!filters) {
    return emptyArray;
  }

  const include = (filter: ComparablesFilterFields): boolean => !exclusions?.has(filter);

  return Object.entries(filters).flatMap(
    ([key, value]) =>
      (include(key as ComparablesFilterFields) &&
        filterTypes[key as keyof ComparablesFilterOptions]?.(value as never)) ||
      []
  );
}

export function filterRecords<T extends IComparablesTransactionDTO | EnrichedComparableDTO>(
  filters: ComparablesFilterOptions,
  data: T[],
  dataSource: "hmlr" | "hometrack" = "hmlr",
  displayBy: PriceModes | undefined = undefined
): {
  includedResults: T[];
  excludedResults: T[];
} {
  const filterFunctions = createFilters(
    filters,
    new Set([
      ...disabledEPCFields,
      ...hiddenFieldsByFeature.flatMap((check) => (check[0]() ? Array.from(check[1]) : [])),
    ])
  );

  if (!filterFunctions.length) {
    return {
      includedResults: data,
      excludedResults: [],
    };
  }

  return data.reduce<{
    includedResults: T[];
    excludedResults: T[];
  }>(
    (acc: { includedResults: T[]; excludedResults: T[] }, item: T) => {
      // Special handling for Zoopla-only records
      const isZooplaOnly = "zooplaEnrichment" in item && item.zooplaEnrichment?.correlationStatus === "zoopla_only";

      const hasZooplaData = "zooplaEnrichment" in item && item.zooplaEnrichment?.correlationStatus !== "no_zoopla";

      // If the data source is hometrack filter only items that have Zoopla data
      if (dataSource === "hometrack" && isZoopla(displayBy)) {
        if (!hasZooplaData) {
          acc.excludedResults.push(item);
          return acc;
        }

        // Zoopla filter functions
        const zooplaFilterFunctions = createZooplaFilters(
          filters,
          new Set([
            ...disabledZooplaFields,
            ...hiddenFieldsByFeature.flatMap((check) => (check[0]() ? Array.from(check[1]) : [])),
          ]),
          displayBy
        );
        const zooplaItem = {
          uprn: item.uprn,
          attributes: item.zooplaEnrichment?.attributes,
          valuation: item.zooplaEnrichment?.valuation,
        } as ZooplaComparableDTO;

        if (zooplaFilterFunctions.every((filterFn) => filterFn(zooplaItem))) {
          acc.includedResults.push(item);
        } else {
          acc.excludedResults.push(item);
        }
      } else {
        // Else filter items that have no LT data or are Zoopla only
        if (!isZooplaOnly && filterFunctions.every((filterFn) => filterFn(item))) {
          acc.includedResults.push(item);
        } else {
          acc.excludedResults.push(item);
        }
      }

      return acc;
    },
    { includedResults: [], excludedResults: [] }
  );
}
